import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import {
  Search, X, TrendingUp, Users, Building2, Home, User, UserCheck,
  Calendar, Landmark, ChevronDown, ChevronRight, MapPin
} from "lucide-react";
import IndiaMap from "../components/IndiaMap";
import { getThemeDotColor } from "../utils/dynamicColors";
import { fmtOutreach, displayTheme } from "../utils/format";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/* ── SDG color + number mapping ─────────────────────────────────────── */
const SDG_MAP = {
  "No poverty":                              { num: 1,  color: "#E5243B" },
  "Zero hunger":                             { num: 2,  color: "#DDA63A" },
  "Good health and well-being":              { num: 3,  color: "#4C9F38" },
  "Quality education":                       { num: 4,  color: "#C5192D" },
  "Gender equality":                         { num: 5,  color: "#FF3A21" },
  "Clean water and sanitation":              { num: 6,  color: "#26BDE2" },
  "Affordable and clean energy":             { num: 7,  color: "#FCC30B" },
  "Decent work and economic growth":         { num: 8,  color: "#A21942" },
  "Industry, innovation and infrastructure": { num: 9,  color: "#FD6925" },
  "Reduced inequalities":                    { num: 10, color: "#DD1367" },
  "Sustainable cities and communities":      { num: 11, color: "#FD9D24" },
  "Sustainable cities and economies":        { num: 11, color: "#FD9D24" },
  "Responsible consumption and production":  { num: 12, color: "#BF8B2E" },
  "Climate action":                          { num: 13, color: "#3F7E44" },
  "Life below water":                        { num: 14, color: "#0A97D9" },
  "Life on land":                            { num: 15, color: "#56C02B" },
  "Peace, justice and strong institutions":  { num: 16, color: "#00689D" },
  "Partnership for the goals":               { num: 17, color: "#19486A" },
};

const sdgInfo = (name) => SDG_MAP[name] || { num: "?", color: "#94a3b8" };

export default function GrantsOverview() {
  const [searchParams] = useSearchParams();
  const [grantsList, setGrantsList] = useState([]);
  const [selectedGrantId, setSelectedGrantId] = useState(searchParams.get("grant") || "");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Map drill state
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [mapViewMode, setMapViewMode] = useState("states");

  // Geo tree expand state
  const [expandedStates, setExpandedStates] = useState({});
  const [expandedDistricts, setExpandedDistricts] = useState({});

  useEffect(() => {
    axios.get(`${API}/grant-overview/list`).then(r => {
      setGrantsList(r.data);
      // Auto-select the grant with the latest approved date if none selected
      if (!selectedGrantId && r.data.length > 0) {
        const withDate = r.data.filter(g => g.approved_date);
        if (withDate.length > 0) {
          const latest = withDate.reduce((a, b) => (a.approved_date > b.approved_date ? a : b));
          setSelectedGrantId(latest.id);
        }
      }
    }).catch(console.error);
  }, []);

  useEffect(() => {
    const grantFromUrl = searchParams.get("grant");
    if (grantFromUrl && grantFromUrl !== selectedGrantId) {
      setSelectedGrantId(grantFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!selectedGrantId) { setDetail(null); return; }
    setLoading(true);
    setSelectedState(null);
    setSelectedDistrict(null);
    setMapViewMode("states");
    setExpandedStates({});
    setExpandedDistricts({});
    axios.get(`${API}/grant-overview/detail`, { params: { grant_id: selectedGrantId } })
      .then(r => setDetail(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedGrantId]);

  // Proportional allocation: distribute sanctioned amount across geographies
  // Weight = number of leaf-level entries (blocks > districts > states)
  const stateWeights = useMemo(() => {
    if (!detail?.geographies) return {};
    const geos = detail.geographies;
    const hasAnyBlocks = geos.some(g => g.block_count > 0);
    const hasAnyDistricts = geos.some(g => g.district_count > 0);
    const weights = {};
    let total = 0;
    geos.forEach(g => {
      const w = hasAnyBlocks ? Math.max(g.block_count, 1)
              : hasAnyDistricts ? Math.max(g.district_count, 1)
              : 1;
      weights[g.state] = w;
      total += w;
    });
    // Normalize to fractions
    Object.keys(weights).forEach(k => { weights[k] = weights[k] / (total || 1); });
    return weights;
  }, [detail]);

  const stateData = useMemo(() => {
    if (!detail?.geographies) return [];
    return detail.geographies.map(g => ({
      state: g.state, grants: 1,
      allocation_cr: Math.round(detail.sanctioned_cr * (stateWeights[g.state] || 0) * 100) / 100,
      districts: g.district_count, blocks: g.block_count,
    }));
  }, [detail, stateWeights]);

  const districtData = useMemo(() => {
    if (!detail?.geographies || !selectedState) return [];
    const stGeo = detail.geographies.find(g => g.state === selectedState);
    if (!stGeo) return [];
    const stAlloc = detail.sanctioned_cr * (stateWeights[selectedState] || 0);
    const hasBlocks = stGeo.districts.some(d => d.blocks?.length > 0);
    let totalW = 0;
    const dWeights = stGeo.districts.map(d => {
      const w = hasBlocks ? Math.max(d.blocks?.length || 0, 1) : 1;
      totalW += w;
      return w;
    });
    return stGeo.districts.map((d, i) => ({
      district: d.name, grants: 1,
      allocation_cr: Math.round(stAlloc * (dWeights[i] / (totalW || 1)) * 100) / 100,
      blocks: d.blocks.length,
    }));
  }, [detail, selectedState, stateWeights]);

  const blockData = useMemo(() => {
    if (!detail?.geographies || !selectedState || !selectedDistrict) return [];
    const stGeo = detail.geographies.find(g => g.state === selectedState);
    if (!stGeo) return [];
    const dist = stGeo.districts.find(d => d.name === selectedDistrict);
    if (!dist || !dist.blocks?.length) return [];
    // District's proportional share
    const stAlloc = detail.sanctioned_cr * (stateWeights[selectedState] || 0);
    const hasBlocks = stGeo.districts.some(d => d.blocks?.length > 0);
    let totalDW = 0;
    let thisDW = 0;
    stGeo.districts.forEach(d => {
      const w = hasBlocks ? Math.max(d.blocks?.length || 0, 1) : 1;
      totalDW += w;
      if (d.name === selectedDistrict) thisDW = w;
    });
    const distAlloc = stAlloc * (thisDW / (totalDW || 1));
    const perBlock = dist.blocks.length > 0 ? distAlloc / dist.blocks.length : 0;
    return dist.blocks.map(b => ({
      block: b, grants: 1,
      allocation_cr: Math.round(perBlock * 100) / 100,
    }));
  }, [detail, selectedState, selectedDistrict, stateWeights]);

  const filtered = useMemo(() => {
    if (!searchTerm) return grantsList;
    const q = searchTerm.toLowerCase();
    return grantsList.filter(g => g.label.toLowerCase().includes(q));
  }, [grantsList, searchTerm]);

  // All-district data for district view mode on the map
  const allDistrictMapData = useMemo(() => {
    if (!detail?.geographies) return [];
    const result = [];
    detail.geographies.forEach(g => {
      const stAlloc = detail.sanctioned_cr * (stateWeights[g.state] || 0);
      const hasBlocks = g.districts.some(d => d.blocks?.length > 0);
      let totalDW = 0;
      const dWeights = g.districts.map(d => {
        const w = hasBlocks ? Math.max(d.blocks?.length || 0, 1) : 1;
        totalDW += w;
        return w;
      });
      g.districts.forEach((d, i) => {
        result.push({
          district: d.name,
          state: g.state,
          allocation_cr: Math.round(stAlloc * (dWeights[i] / (totalDW || 1)) * 100) / 100,
          grants: 1,
        });
      });
    });
    return result;
  }, [detail, stateWeights]);

  const handleStateSelect = (state) => { setSelectedState(state || null); setSelectedDistrict(null); };
  const handleDistrictSelect = (district) => { setSelectedDistrict(district || null); };

  const toggleState = (st) => setExpandedStates(p => ({ ...p, [st]: !p[st] }));
  const toggleDistrict = (key) => setExpandedDistricts(p => ({ ...p, [key]: !p[key] }));

  return (
    <div className="space-y-5" data-testid="grants-overview-page">
      {/* Grant Selector */}
      <div className="relative" data-testid="grant-selector">
        <div className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-3">
          <Search size={16} className="text-slate-600 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search and select a grant..."
            value={dropdownOpen ? searchTerm : (grantsList.find(g => g.id === selectedGrantId)?.label || searchTerm)}
            onChange={e => { setSearchTerm(e.target.value); setDropdownOpen(true); }}
            onFocus={() => setDropdownOpen(true)}
            className="flex-1 text-sm text-slate-800 bg-transparent outline-none placeholder:text-slate-600"
            data-testid="grant-search-input"
          />
          <span className="text-xs font-medium text-slate-600 whitespace-nowrap">{grantsList.length} Grants</span>
          {selectedGrantId && (
            <button onClick={() => { setSelectedGrantId(""); setSearchTerm(""); setDetail(null); }}
              className="text-slate-600 hover:text-slate-600" data-testid="clear-grant-btn"><X size={14} /></button>
          )}
        </div>
        {dropdownOpen && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-96 overflow-y-auto"
            data-testid="grant-dropdown">
            {filtered.length === 0 ? (
              <div className="px-4 py-3 text-sm text-slate-600">No grants found</div>
            ) : filtered.map(g => (
              <button key={g.id}
                onClick={() => { setSelectedGrantId(g.id); setSearchTerm(""); setDropdownOpen(false); }}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${selectedGrantId === g.id ? "bg-red-50 text-[#006837]" : "text-slate-700"}`}
                data-testid={`grant-option-${g.id}`}>
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: getThemeDotColor(g.theme) }} />
                <span className="truncate">{g.label}</span>
                <span className={`text-[9px] ml-auto flex-shrink-0 px-1.5 py-0.5 rounded-full ${
                  g.status === "Ongoing" ? "bg-green-50 text-green-600" : g.status === "Closed" ? "bg-slate-100 text-slate-500" : "bg-amber-50 text-amber-600"
                }`}>{g.status}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {dropdownOpen && <div className="fixed inset-0 z-40" onClick={() => setDropdownOpen(false)} />}

      {!selectedGrantId && !loading && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
          <Search size={40} className="text-slate-200 mx-auto mb-3" />
          <div className="text-sm text-slate-500 font-medium">Select a grant from the dropdown to view details</div>
        </div>
      )}

      {loading && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#006837] mx-auto mb-3" />
          <div className="text-sm text-slate-500">Loading grant details...</div>
        </div>
      )}

      {detail && !loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
          {/* Map (left, equal width) */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid="grant-map-section">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <div className="font-semibold text-slate-900 text-sm">Grant Geography</div>
                <span className="text-[11px] text-slate-600 font-medium">
                  {detail.state_count} state{detail.state_count !== 1 ? "s" : ""}, {detail.district_count} district{detail.district_count !== 1 ? "s" : ""}, {detail.block_count} block{detail.block_count !== 1 ? "s" : ""}
                </span>
              </div>
              {selectedState && (
                <button onClick={() => { setSelectedState(null); setSelectedDistrict(null); }}
                  className="text-xs px-2.5 py-1 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                  data-testid="back-all-states-btn">
                  &#8592; All States
                </button>
              )}
            </div>
            <IndiaMap
              stateData={stateData}
              districtData={districtData}
              blockData={blockData}
              allDistrictData={allDistrictMapData}
              onStateSelect={handleStateSelect}
              onDistrictSelect={handleDistrictSelect}
              selectedState={selectedState}
              selectedDistrict={selectedDistrict}
              selectedDistShapeID={null}
              dataLoading={false}
              mapViewMode={mapViewMode}
              onMapViewChange={(mode) => { setMapViewMode(mode); if (selectedState) { setSelectedState(null); setSelectedDistrict(null); } }}
            />
          </div>

          {/* ════════════ Detail Panel (right, 3 cols) ════════════ */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-y-auto" style={{ maxHeight: 720 }} data-testid="grant-detail-panel">
            <div className="p-6 space-y-5">

              {/* ── Header ── */}
              <div data-testid="grant-header-section">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-bold text-slate-900 leading-snug" data-testid="grant-id-title">{detail.grant_id}</h3>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${
                    detail.status === "Ongoing" ? "bg-green-50 text-green-700 border-green-200" :
                    detail.status === "Closed" ? "bg-slate-100 text-slate-600 border-slate-200" :
                    "bg-amber-50 text-amber-700 border-amber-200"
                  }`} data-testid="grant-status-badge">{detail.status}</span>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full text-white" style={{ background: getThemeDotColor(detail.theme) }} data-testid="grant-theme-badge">
                    {displayTheme(detail.theme)}
                  </span>
                  {detail.fy && <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-50 text-slate-500 border border-slate-200" data-testid="grant-fy-badge">{detail.fy}</span>}
                </div>
                {detail.project_title && (
                  <p className="text-sm text-slate-600 mt-1.5 leading-relaxed" data-testid="project-title">{detail.project_title}</p>
                )}
                <p className="text-xs font-semibold text-slate-700 mt-1">{detail.grantee_name}</p>

                {/* FY, dates, PO, Source of Funds — all in one row */}
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {detail.project_start_date && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border border-slate-200 bg-white text-slate-700" data-testid="date-chip-start">
                      <Calendar size={11} className="text-slate-600 flex-shrink-0" />
                      <span className="text-slate-600">Start:</span>
                      <span className="font-semibold">{detail.project_start_date}</span>
                    </span>
                  )}
                  {detail.project_end_date && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border border-slate-200 bg-white text-slate-700" data-testid="date-chip-end">
                      <Calendar size={11} className="text-slate-600 flex-shrink-0" />
                      <span className="text-slate-600">End:</span>
                      <span className="font-semibold">{detail.project_end_date}</span>
                    </span>
                  )}
                  {detail.program_officer && (
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200" data-testid="grant-po-badge">
                      Grant Anchor: {detail.program_officer}
                    </span>
                  )}
                  {detail.source_of_fund && (
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 border border-violet-200" data-testid="grant-fund-badge">
                      {detail.source_of_fund}
                    </span>
                  )}
                </div>
              </div>

              {/* ── KPIs ── */}
              <div data-testid="grant-kpis-section">
                <div className="flex flex-wrap items-stretch gap-3">
                  <div className="bg-slate-50 rounded-lg border border-slate-100 px-4 py-3 min-w-[130px]">
                    <div className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider">Sanctioned</div>
                    <div className="text-base font-bold mt-1 text-[#006837]">{"\u20B9"}{detail.sanctioned_cr} Cr</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg border border-slate-100 px-4 py-3 min-w-[150px]">
                    <div className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider">Disbursed</div>
                    <div className="text-base font-bold mt-1 text-emerald-700">
                      {"\u20B9"}{detail.disbursed_cr} Cr
                      <span className="text-xs font-medium text-slate-600 ml-1">({detail.disbursement_pct}%)</span>
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-lg border border-slate-100 px-4 py-3 min-w-[150px]">
                    <div className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider">Utilised</div>
                    <div className="text-base font-bold mt-1 text-amber-700">
                      {"\u20B9"}{detail.utilised_cr ?? 0} Cr
                      <span className="text-xs font-medium text-slate-600 ml-1">({detail.utilisation_pct ?? 0}%)</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── SDG Chips ── */}
              {detail.sdg?.length > 0 && (
                <div className="flex flex-wrap items-center gap-2" data-testid="grant-sdg-chips">
                  {detail.sdg.map((name, i) => {
                    const { num, color } = sdgInfo(name);
                    return (
                      <span key={i}
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border border-slate-200 bg-white text-slate-700"
                        data-testid={`sdg-chip-${num}`}
                      >
                        <span className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[8px] font-bold flex-shrink-0"
                          style={{ background: color }}>
                          {num}
                        </span>
                        SDG {num}: {name}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* ── Outreach ── */}
              {detail.outreach?.has_data && (
                <div className="bg-slate-50/60 rounded-xl border border-slate-100 p-5" data-testid="grant-outreach-section">
                  <div className="flex items-center gap-2 mb-3">
                    <Users size={16} className="text-slate-600" />
                    <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Outreach</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2.5">
                    {[
                      { icon: Building2, value: detail.outreach.institutions, label: "Institutions", bg: "bg-blue-50", fg: "text-blue-600" },
                      { icon: UserCheck, value: detail.outreach.user_groups, label: "User Groups", bg: "bg-violet-50", fg: "text-violet-600" },
                      { icon: Home, value: detail.outreach.households, label: "HHs", bg: "bg-amber-50", fg: "text-amber-600" },
                      { icon: User, value: detail.outreach.individuals, label: "Individuals", bg: "bg-green-50", fg: "text-green-600" },
                    ].map(({ icon: Icon, value, label, bg, fg }, i) => (
                      <div key={i} className="bg-white rounded-lg border border-slate-100 px-3 py-4 text-center">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2 ${bg}`}>
                          <Icon size={18} className={fg} />
                        </div>
                        <div className="text-xl font-bold text-slate-800" title={(value || 0).toLocaleString("en-IN")}>{fmtOutreach(value)}</div>
                        <div className="text-xs text-slate-500 mt-1">{label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── MEAS ── */}
              {detail.meas_count > 0 && (
                <div className="bg-slate-50/60 rounded-xl border border-slate-100 p-5" data-testid="grant-meas-section">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <TrendingUp size={14} className="text-slate-600" />
                    <span className="text-xs font-semibold text-slate-500 tracking-wide">Key Performance Indicators (KPIs)</span>
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-slate-100 text-slate-500 border-slate-200">
                      {detail.meas_count} indicators
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {detail.indicator_types?.map((t, i) => (
                      <span key={i} className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${
                        t.type === "Activity" ? "bg-blue-50 text-blue-700 border-blue-200" :
                        t.type === "Output" ? "bg-amber-50 text-amber-700 border-amber-200" :
                        t.type === "Outcome" ? "bg-green-50 text-green-700 border-green-200" :
                        "bg-slate-100 text-slate-600 border-slate-200"
                      }`}>{t.type}: {t.count.toLocaleString("en-IN")}</span>
                    ))}
                  </div>
                  {/* Project Performance KPI */}
                  {(
                    <div className="grid grid-cols-2 gap-3" data-testid="project-performance-kpi">
                      <div className="bg-white rounded-lg border border-slate-200 px-4 py-3">
                        <div className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider">YTD Performance</div>
                        <div className={`text-lg font-bold mt-1 ${detail.project_perf_ytd >= 100 ? "text-green-600" : detail.project_perf_ytd >= 50 ? "text-amber-600" : detail.project_perf_ytd > 0 ? "text-red-600" : "text-slate-600"}`}>
                          {detail.project_perf_ytd}%
                        </div>
                        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-1.5">
                          <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(detail.project_perf_ytd, 100)}%`, background: detail.project_perf_ytd >= 100 ? "#16a34a" : detail.project_perf_ytd >= 50 ? "#d97706" : "#dc2626" }} />
                        </div>
                      </div>
                      <div className="bg-white rounded-lg border border-slate-200 px-4 py-3">
                        <div className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider">PTD Performance</div>
                        <div className={`text-lg font-bold mt-1 ${detail.project_perf_ptd >= 100 ? "text-green-600" : detail.project_perf_ptd >= 50 ? "text-amber-600" : detail.project_perf_ptd > 0 ? "text-red-600" : "text-slate-600"}`}>
                          {detail.project_perf_ptd}%
                        </div>
                        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-1.5">
                          <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(detail.project_perf_ptd, 100)}%`, background: detail.project_perf_ptd >= 100 ? "#16a34a" : detail.project_perf_ptd >= 50 ? "#d97706" : "#dc2626" }} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Geography Tree ── */}
              {detail.geographies?.length > 0 && (
                <div className="bg-slate-50/60 rounded-xl border border-slate-100 p-5" data-testid="grant-geo-tree-section">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin size={14} className="text-slate-600" />
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">States, Districts & Blocks</span>
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                      {detail.state_count}S &middot; {detail.district_count}D &middot; {detail.block_count}B
                    </span>
                  </div>
                  <div className="space-y-1">
                    {detail.geographies.map((geo, si) => {
                      const stKey = geo.state;
                      const isOpen = expandedStates[stKey];
                      const hasChildren = geo.districts?.length > 0;
                      return (
                        <div key={si}>
                          <button
                            onClick={() => hasChildren && toggleState(stKey)}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
                              isOpen ? "bg-white border border-slate-200 shadow-sm" : "hover:bg-white"
                            }`}
                            data-testid={`geo-state-${stKey}`}
                          >
                            {hasChildren ? (
                              isOpen ? <ChevronDown size={12} className="text-slate-600 flex-shrink-0" />
                                     : <ChevronRight size={12} className="text-slate-600 flex-shrink-0" />
                            ) : (
                              <div className="w-3 flex-shrink-0" />
                            )}
                            <MapPin size={13} className="text-[#006837] flex-shrink-0" />
                            <span className="text-sm font-semibold text-slate-800">{geo.state}</span>
                            <span className="text-[10px] text-slate-600 ml-auto flex-shrink-0">
                              {geo.district_count > 0 && `${geo.district_count}d`}
                              {geo.district_count > 0 && geo.block_count > 0 && " · "}
                              {geo.block_count > 0 && `${geo.block_count}b`}
                            </span>
                          </button>

                          {isOpen && geo.districts?.length > 0 && (
                            <div className="ml-5 mt-0.5 pl-3 border-l-2 border-slate-200 space-y-0.5">
                              {geo.districts.map((dist, di) => {
                                const dKey = `${stKey}__${dist.name}`;
                                const dOpen = expandedDistricts[dKey];
                                const hasBlocks = dist.blocks?.length > 0;
                                return (
                                  <div key={di}>
                                    <button
                                      onClick={() => hasBlocks && toggleDistrict(dKey)}
                                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-left transition-colors ${
                                        dOpen ? "bg-white shadow-sm border border-slate-100" : "hover:bg-white"
                                      }`}
                                      data-testid={`geo-district-${dist.name}`}
                                    >
                                      {hasBlocks ? (
                                        dOpen ? <ChevronDown size={10} className="text-slate-600 flex-shrink-0" />
                                               : <ChevronRight size={10} className="text-slate-600 flex-shrink-0" />
                                      ) : (
                                        <div className="w-2.5 flex-shrink-0" />
                                      )}
                                      <span className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
                                      <span className="text-xs font-medium text-slate-700">{dist.name}</span>
                                      {hasBlocks && (
                                        <span className="text-[10px] text-slate-600 ml-auto flex-shrink-0">{dist.blocks.length}b</span>
                                      )}
                                    </button>

                                    {dOpen && dist.blocks?.length > 0 && (
                                      <div className="ml-5 mt-0.5 pl-2.5 border-l border-dashed border-slate-200 space-y-0.5">
                                        {dist.blocks.map((blk, bi) => (
                                          <div key={bi} className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-white transition-colors"
                                            data-testid={`geo-block-${blk}`}>
                                            <span className="w-1 h-1 rounded-full bg-slate-300 flex-shrink-0" />
                                            <span className="text-[11px] text-slate-600">{blk}</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}
    </div>
  );
}
